import CloseIcon from "@mui/icons-material/Close";
import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import Dialog from "@mui/material/Dialog";
import IconButton from "@mui/material/IconButton";
import InputBase from "@mui/material/InputBase";
import Typography from "@mui/material/Typography";
import { useEffect, useMemo, useState } from "react";

import { TestIds } from "@storyboard/web-test-ids/testIds";
import type { DispatchTableRow } from "@/hooks/dispatch/dispatchTableRow";
import { DRIVER_MANAGER_TAG_SLUG, reassignOptionsFor } from "@/hooks/dispatch/reassignOptions";
import type { OfficeMember } from "@/hooks/dispatch/useOfficeMembers";
import type { AssignmentTarget } from "@/store/types";
import { zinc } from "@/ui/tokens";
import { DISPATCH_ASSIGNED } from "./dispatchColors";

const NOTE_LIMIT = 140;

const buttonSx = {
  height: 34,
  px: 1.75,
  borderRadius: 1,
  fontSize: "0.875rem",
  fontWeight: 600,
} as const;

const fieldSx = {
  width: "100%",
  px: 1.25,
  py: 0.75,
  borderRadius: 1,
  bgcolor: zinc[800],
  border: `1px solid ${zinc[700]}`,
  color: "white",
  fontSize: "0.875rem",
} as const;

/** One entry the picker lists: a target, or `null` for "Clear assignment". */
type Entry = { key: string; label: string; role: string; target: AssignmentTarget | null };

/** Who handed the highlighted row off, when it carries an open assignment: what "Return to …" sends it back to. */
export type ReassignCurrent = { assignerMembershipId: string | null; assignerName: string | null };

/** "1 message", "3 drivers": what the picker is reassigning. */
function targetSummary(rows: readonly DispatchTableRow[]): string {
  const noun = rows.every((row) => row.kind === "message") ? "message" : "driver";
  return `${rows.length} ${noun}${rows.length === 1 ? "" : "s"}`;
}

/**
 * The reassignment prototype's picker: who to hand the highlighted or ticked rows to, and an optional note.
 *
 * Lists the drivers' shared Driver Manager book (preselected), "Me", the other Dispatch views as teams, and the office
 * members, narrowed as the dispatcher types; the arrows walk the list and Enter hands off to the highlighted entry. When
 * the row already carries an open assignment, "Return to <assigner>" and "Clear assignment" lead the list. Escape
 * closes without writing.
 */
export default function DispatchReassignDialog({
  open,
  rows,
  me,
  views,
  currentViewId,
  members,
  current,
  onClose,
  onSubmit,
}: {
  open: boolean;
  /** The rows being reassigned: the highlighted row, or every ticked row. */
  rows: readonly DispatchTableRow[];
  me: string;
  views: readonly { id: string; name: string }[];
  currentViewId: string;
  members: readonly OfficeMember[];
  /** The highlighted row's open assignment, when it has one and only one row is being reassigned. */
  current: ReassignCurrent | null;
  onClose: () => void;
  /** Hands the rows to `assignee`, or clears their assignment when it is `null`. */
  onSubmit: (assignee: AssignmentTarget | null, note: string | null) => void;
}) {
  const [query, setQuery] = useState("");
  const [note, setNote] = useState("");
  const [active, setActive] = useState(0);

  const leading = useMemo<Entry[]>(() => {
    if (!current) return [];
    const entries: Entry[] = [];
    if (current.assignerMembershipId) {
      entries.push({
        key: "return",
        label: `Return to ${current.assignerMembershipId === me ? "me" : (current.assignerName ?? "sender")}`,
        role: "Sender",
        target: { kind: "member", membershipId: current.assignerMembershipId },
      });
    }
    entries.push({ key: "clear", label: "Clear assignment", role: "Back to unassigned", target: null });
    return entries;
  }, [current, me]);

  const options = useMemo(
    () => reassignOptionsFor({ rows, dmTagSlug: DRIVER_MANAGER_TAG_SLUG, me, views, currentViewId, members, query }),
    [rows, me, views, currentViewId, members, query],
  );

  // The leading entries only while nothing is typed: a search is for a new owner.
  const entries = useMemo<Entry[]>(
    () => [
      ...(query.trim() === "" ? leading : []),
      ...options.map((option, index) => ({
        key: `option-${index}`,
        label: option.label,
        role: option.role,
        target: option.target,
      })),
    ],
    [leading, options, query],
  );

  // On opening nothing is typed, so the preselected Driver Manager entry sits right after the leading ones.
  const preselected = options.some((option) => option.preselected) ? leading.length : 0;

  // Each opening starts afresh, on the preselected entry.
  // biome-ignore lint/correctness/useExhaustiveDependencies: only an opening resets; typing narrows `preselected`'s list
  useEffect(() => {
    if (!open) return;
    setQuery("");
    setNote("");
    setActive(preselected);
  }, [open]);

  const clamped = Math.min(active, Math.max(entries.length - 1, 0));
  const chosen = entries[clamped];

  const submit = (entry: Entry | undefined) => {
    if (!entry || rows.length === 0) return;
    onSubmit(entry.target, note.trim() === "" ? null : note.trim());
  };

  const onListKey = (event: React.KeyboardEvent) => {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActive(Math.min(clamped + 1, entries.length - 1));
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setActive(Math.max(clamped - 1, 0));
    } else if (event.key === "Enter" && !event.nativeEvent.isComposing) {
      event.preventDefault();
      submit(chosen);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      slotProps={{
        paper: {
          "data-sb": TestIds.dispatch.reassignDialog,
          sx: { width: 440, maxWidth: "94vw", bgcolor: zinc[900], color: "white", border: `1px solid ${zinc[700]}` },
        } as React.ComponentProps<typeof Dialog>["PaperProps"],
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", px: 2.5, pt: 2, pb: 1 }}>
        <Typography component="h2" sx={{ fontSize: "1.0625rem", fontWeight: 700, flex: 1 }}>
          Reassign to…
        </Typography>
        <IconButton
          size="small"
          aria-label="Close"
          onClick={onClose}
          sx={{ color: zinc[400], "&:hover": { color: "white" } }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>
      <Box sx={{ px: 2.5, pb: 2.5, display: "flex", flexDirection: "column", gap: 1.25 }}>
        <InputBase
          autoFocus
          placeholder="Search people and teams"
          value={query}
          onChange={(event) => {
            setQuery(event.target.value);
            setActive(0);
          }}
          onKeyDown={onListKey}
          inputProps={{ "aria-label": "Search people and teams" }}
          sx={fieldSx}
        />
        <Box
          role="listbox"
          aria-label="Reassign to"
          sx={{ maxHeight: 280, overflowY: "auto", display: "flex", flexDirection: "column", gap: 0.25 }}
        >
          {entries.length === 0 && (
            <Typography sx={{ fontSize: "0.8125rem", color: zinc[400], px: 1, py: 1 }}>No one matches.</Typography>
          )}
          {entries.map((entry, index) => (
            <ButtonBase
              key={entry.key}
              role="option"
              aria-selected={index === clamped}
              data-sb={TestIds.dispatch.reassignOption}
              onMouseEnter={() => setActive(index)}
              onClick={() => submit(entry)}
              sx={{
                justifyContent: "flex-start",
                textAlign: "left",
                px: 1,
                py: 0.75,
                borderRadius: 1,
                gap: 1,
                bgcolor: index === clamped ? zinc[700] : "transparent",
                borderLeft: `3px solid ${index === clamped ? DISPATCH_ASSIGNED.accent : "transparent"}`,
              }}
            >
              <Box sx={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
                <Typography sx={{ fontSize: "0.875rem", fontWeight: 600, color: "white" }} noWrap>
                  {entry.label}
                </Typography>
                <Typography sx={{ fontSize: "0.75rem", color: zinc[400] }} noWrap>
                  {entry.role}
                </Typography>
              </Box>
            </ButtonBase>
          ))}
        </Box>
        <InputBase
          placeholder="Add a note (optional)"
          value={note}
          onChange={(event) => setNote(event.target.value.slice(0, NOTE_LIMIT))}
          onKeyDown={onListKey}
          inputProps={{ "aria-label": "Note", "data-sb": TestIds.dispatch.reassignNote }}
          sx={fieldSx}
        />
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography sx={{ fontSize: "0.8125rem", color: zinc[400], flex: 1 }}>
            Reassigning {targetSummary(rows)}
          </Typography>
          <ButtonBase onClick={onClose} sx={{ ...buttonSx, color: zinc[300], "&:hover": { color: "white" } }}>
            Cancel
          </ButtonBase>
          <ButtonBase
            onClick={() => submit(chosen)}
            disabled={!chosen || rows.length === 0}
            sx={{
              ...buttonSx,
              bgcolor: DISPATCH_ASSIGNED.strong,
              color: "white",
              "&:hover": { filter: "brightness(1.1)" },
              "&:disabled": { opacity: 0.5 },
            }}
          >
            {chosen?.target === null ? "Clear" : "Reassign"}
          </ButtonBase>
        </Box>
      </Box>
    </Dialog>
  );
}
